import { render } from "../ui/app.js";

import { C, ESTADOS, ESTADO_COLOR } from "../core/config.js";
import { state } from "../core/state.js";
import { escapeHTML, fmtCOP, fmtDate, normalize, parseNumber, today } from "../core/utils.js";
import { getSaldoClase } from "./contabilidad.js";
import { getLiquidacionOP, renderDetalleOP } from "./documentos.js";

export function getPeriodoRango(periodo) {
  const hoy = today();
  const now = new Date(hoy + "T12:00:00");
  let desde = hoy, hasta = hoy;
  if (periodo === "dia") {
    desde = hoy; hasta = hoy;
  } else if (periodo === "semana") {
    const dow = now.getDay() === 0 ? 6 : now.getDay() - 1; // 0=lunes
    const lunes = new Date(now); lunes.setDate(now.getDate() - dow);
    desde = lunes.toISOString().slice(0, 10); hasta = hoy;
  } else if (periodo === "mes") {
    desde = hoy.slice(0, 7) + "-01"; hasta = hoy;
  } else if (periodo === "trimestre") {
    const q = Math.floor(now.getMonth() / 3);
    const ini = new Date(now.getFullYear(), q * 3, 1);
    desde = ini.toISOString().slice(0, 10); hasta = hoy;
  } else if (periodo === "semestre") {
    const s = now.getMonth() < 6 ? 0 : 6;
    const ini = new Date(now.getFullYear(), s, 1);
    desde = ini.toISOString().slice(0, 10); hasta = hoy;
  } else if (periodo === "anio") {
    desde = now.getFullYear() + "-01-01"; hasta = hoy;
  } else { // "todo"
    desde = ""; hasta = "";
  }
  return { desde, hasta };
}

export function calcularMargenPeriodoOps(desde, hasta) {
  // VENTAS: facturas emitidas en el período (excluye anuladas)
  const facturasPeriodo = (state.data.facturas || []).filter(f => {
    if (f.estado === "anulada" || f.estado === "cancelada") return false;
    const fecha = (f.fecha || f.fecha_emision || "");
    if (desde && fecha < desde) return false;
    if (hasta && fecha > hasta) return false;
    return true;
  });
  const ventaTotal = facturasPeriodo.reduce((s, f) => s + (parseNumber(f.total) || 0), 0);

  // COSTOS: OPs activas en el período (fecha de la OP)
  const opsPeriodo = (state.data.pedidos || []).filter(op => {
    if (op.estado === "cancelada") return false;
    const fecha = op.fecha || "";
    if (desde && fecha < desde) return false;
    if (hasta && fecha > hasta) return false;
    return true;
  });
  let costoTotal = 0;
  opsPeriodo.forEach(op => {
    const liq = getLiquidacionOP(op.id);
    if (liq) costoTotal += liq.costoTotal || 0;
  });

  // GASTOS: de contabilidad (cuentas clase 5) — si hay asientos registrados
  const gastosContab = getSaldoClase("5", desde, hasta);

  // Margen según fórmula: 1 - (costos+gastos) / ventas
  const totalCostosGastos = costoTotal + gastosContab;
  const margen = ventaTotal > 0 ? (1 - totalCostosGastos / ventaTotal) * 100 : null;

  return {
    ventaTotal, costoTotal, gastosContab, totalCostosGastos, margen,
    nFacturas: facturasPeriodo.length, nOps: opsPeriodo.length
  };
}

export function calcularDiasRestantes(fechaVigencia) {
  if (!fechaVigencia) return null;
  try {
    const hoy = new Date(today());
    const vigencia = new Date(fechaVigencia);
    
    // Validar que la fecha sea válida
    if (isNaN(vigencia.getTime())) return null;
    
    const difMs = vigencia - hoy;
    const difDias = Math.ceil(difMs / (1000 * 60 * 60 * 24));
    return difDias;
  } catch (error) {
    console.error("Error en calcularDiasRestantes:", error);
    return null;
  }
}

export function getEstadoVigencia(fechaVigencia) {
  const diasRestantes = calcularDiasRestantes(fechaVigencia);
  if (diasRestantes === null) return "sin_vigencia";
  if (diasRestantes < 0) return "vencida";
  if (diasRestantes <= 2) return "por_vencer";
  return "vigente";
}

export function calcularMargenTotal(items) {
  let costoTotal = 0;
  let ventaTotal = 0;
  // Un ítem con escalas de cantidad no entra: su cantidad la elige el cliente,
  // así que no hay una venta ni un costo únicos que promediar. El margen de
  // cada escala se muestra en su propia fila del formulario.
  (items || []).filter(it => !(Array.isArray(it._escalas) && it._escalas.some(e => (parseNumber(e.cantidad) || 0) > 0))).forEach(it => {
    const cant = parseNumber(it.cantidad) || 0;
    const precio = parseNumber(it.precio_unitario) || 0;
    const prod = (state.data.productos||[]).find(p => p.nombre === it.articulo);
    const costo = prod ? (parseNumber(prod.costo) || 0) : (parseNumber(it._costoManual) || 0);
    // Los costos propios de la línea (marcación, domicilio…) son costo real de
    // ese ítem: sin ellos el margen mostrado quedaba por encima del verdadero.
    const costosLinea = (it._costosLinea || []).reduce((s2, c) =>
      s2 + (parseNumber(c.cantidad) || 0) * (parseNumber(c.valor_tercero) || 0), 0);
    costoTotal += cant * costo + costosLinea;
    ventaTotal += cant * precio;
  });
  if (ventaTotal <= 0) return null;
  return { pct: (1 - (costoTotal / ventaTotal)) * 100, costoTotal, ventaTotal };
}

export function calcularFechaPorDias(dias) {
  const n = parseInt(dias, 10);
  if (isNaN(n) || n < 0) return null;
  const base = new Date(today() + "T12:00:00");
  base.setDate(base.getDate() + n);
  const yyyy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, "0");
  const dd = String(base.getDate()).padStart(2, "0");
  const fecha = `${yyyy}-${mm}-${dd}`;
  const diaSemana = base.getDay(); // 0=domingo, 6=sábado
  const esFinDeSemana = diaSemana === 0 || diaSemana === 6;
  const nombreDia = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"][diaSemana];
  return { fecha, esFinDeSemana, nombreDia };
}

export function getCotizacionesPorEstadoVigencia() {
  const cotizaciones = state.data.cotizaciones || [];
  const vigente = [];
  const porVencer = [];
  const vencida = [];
  const sinVigencia = [];
  
  cotizaciones.forEach(function(ct) {
    const estado = getEstadoVigencia(ct.fecha_vigencia);
    const diasRestantes = calcularDiasRestantes(ct.fecha_vigencia);
    const ctConDias = Object.assign({}, ct);
    ctConDias.diasRestantes = diasRestantes;
    
    if (estado === "vigente") vigente.push(ctConDias);
    else if (estado === "por_vencer") porVencer.push(ctConDias);
    else if (estado === "vencida") vencida.push(ctConDias);
    else sinVigencia.push(ctConDias);
  });
  
  return { vigente: vigente, porVencer: porVencer, vencida: vencida, sinVigencia: sinVigencia };
}

export function getAlertasVigencia() {
  const datos = getCotizacionesPorEstadoVigencia();
  // Ya están filtrados en getCotizacionesPorEstadoVigencia
  // No duplicar filtrado
  return {
    porVencer: datos.porVencer,  // Ya están filtrados (0-2 días)
    vencida: datos.vencida       // Ya están filtrados (<0 días)
  };
}

export function renderReporteVigencia() {
  const datos = getCotizacionesPorEstadoVigencia();
  const clientes = state.data.clientes || [];
  
  function crearTablaVigencia(cotizaciones, titulo, color, icono) {
    if (!cotizaciones || cotizaciones.length === 0) {
      return '<div style="background:white;border:1px solid ' + C.border + ';border-radius:6px;padding:11px;text-align:center;color:' + C.muted + ';"><div style="font-size:17px;">✓</div><div style="font-size:14px;font-weight:600;margin-top:8px;">' + titulo + '</div><div style="font-size:11.5px;margin-top:4px;">Sin documentos</div></div>';
    }

    let rows = '';
    cotizaciones.forEach(function(ct) {
      const cliente = clientes.find(function(c) { return c.id === ct.cliente_id; }) || {};
      let diasColor = '#10b981';
      let diasTexto = (ct.diasRestantes || 0) + ' días';
      
      if (ct.diasRestantes < 0) {
        diasColor = C.red;
        diasTexto = Math.abs(ct.diasRestantes || 0) + ' d. vencida';
      } else if (ct.diasRestantes <= 2) {
        diasColor = '#f59e0b';
      }
      
      rows += '<tr style="border-top:1px solid ' + C.border + ';"><td style="padding:9px;font-weight:700;color:' + C.text + ';">' + escapeHTML(ct.numero) + '</td><td style="padding:9px;color:' + C.muted + ';">' + escapeHTML(cliente.nombre || "—") + '</td><td style="padding:9px;color:' + C.muted + ';">' + fmtDate(ct.fecha) + '</td><td style="padding:9px;color:' + C.muted + ';">' + (ct.fecha_vigencia ? fmtDate(ct.fecha_vigencia) : "Sin definir") + '</td><td style="padding:9px;text-align:center;"><span style="background:' + diasColor + '20;border:1px solid ' + diasColor + ';color:' + diasColor + ';padding:4px 12px;border-radius:6px;font-weight:700;font-size:11.5px;">' + (ct.diasRestantes === null ? "—" : diasTexto) + '</span></td><td style="padding:9px;text-align:right;">' + fmtCOP(ct.total) + '</td></tr>';
    });

    return '<div style="background:white;border:1px solid ' + color + '40;border-radius:6px;overflow:hidden;margin-bottom:10px;"><div style="padding:11px;background:' + color + '10;border-bottom:2px solid ' + color + '30;display:flex;align-items:center;gap:10px;"><span style="font-size:17px;">' + icono + '</span><div style="flex:1;"><h3 style="margin:0;font-size:14px;font-weight:700;color:' + C.text + ';">' + titulo + '</h3><p style="margin:4px 0 0;font-size:11.5px;color:' + C.muted + ';">' + cotizaciones.length + ' cotización' + (cotizaciones.length !== 1 ? 'es' : '') + '</p></div><div style="text-align:right;"><div style="font-size:16px;font-weight:800;color:' + color + ';">' + cotizaciones.length + '</div></div></div><div style="overflow-x:auto;"><table style="width:100%;border-collapse:collapse;font-size:12px;"><thead style="background:' + C.border + '20;"><tr style="border-bottom:2px solid ' + C.border + ';"><th style="padding:9px;text-align:left;font-weight:700;color:' + C.text + ';">Número</th><th style="padding:9px;text-align:left;font-weight:700;color:' + C.text + ';">Cliente</th><th style="padding:9px;text-align:left;font-weight:700;color:' + C.text + ';">Fecha CT</th><th style="padding:9px;text-align:left;font-weight:700;color:' + C.text + ';">Vigencia</th><th style="padding:9px;text-align:center;font-weight:700;color:' + C.text + ';">Días</th><th style="padding:9px;text-align:right;font-weight:700;color:' + C.text + ';">Total</th></tr></thead><tbody>' + rows + '</tbody></table></div></div>';
  }

  const totalVigente = (datos.vigente || []).reduce(function(s, ct) { return s + (Number(ct.total) || 0); }, 0);
  const totalPorVencer = (datos.porVencer || []).reduce(function(s, ct) { return s + (Number(ct.total) || 0); }, 0);
  const totalVencida = (datos.vencida || []).reduce(function(s, ct) { return s + (Number(ct.total) || 0); }, 0);

  const resumenes = '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;margin-bottom:10px;"><div style="background:white;border:1px solid ' + C.border + ';border-radius:6px;padding:11px;"><div style="color:' + C.muted + ';font-size:11.5px;font-weight:600;margin-bottom:8px;">✅ VIGENTES</div><div style="font-size:17px;font-weight:800;color:#10b981;margin-bottom:4px;">' + (datos.vigente ? datos.vigente.length : 0) + '</div><div style="font-size:11.5px;color:' + C.muted + ';">' + fmtCOP(totalVigente) + '</div></div><div style="background:white;border:1px solid ' + C.border + ';border-radius:6px;padding:11px;"><div style="color:' + C.muted + ';font-size:11.5px;font-weight:600;margin-bottom:8px;">⚠️ POR VENCER (≤2 días)</div><div style="font-size:17px;font-weight:800;color:#f59e0b;margin-bottom:4px;">' + (datos.porVencer ? datos.porVencer.length : 0) + '</div><div style="font-size:11.5px;color:' + C.muted + ';">' + fmtCOP(totalPorVencer) + '</div></div><div style="background:white;border:1px solid ' + C.border + ';border-radius:6px;padding:11px;"><div style="color:' + C.muted + ';font-size:11.5px;font-weight:600;margin-bottom:8px;">❌ VENCIDAS</div><div style="font-size:17px;font-weight:800;color:' + C.red + ';margin-bottom:4px;">' + (datos.vencida ? datos.vencida.length : 0) + '</div><div style="font-size:11.5px;color:' + C.muted + ';">' + fmtCOP(totalVencida) + '</div></div></div>';

  const header = '<div style="margin-bottom:9px;display:flex;justify-content:space-between;align-items:center;"><div><h2 style="margin:0 0 12px;font-size:16px;color:' + C.text + ';font-weight:700;">📅 Reporte de Vigencia de Cotizaciones</h2><p style="margin:0;color:' + C.muted + ';font-size:11.5px;">Estado de validez de cotizaciones</p></div><button data-action="cerrar-reporte-vigencia" style="background:' + C.border + ';border:none;color:' + C.text + ';padding:10px 20px;border-radius:6px;font-weight:700;cursor:pointer;font-size:12px;">← Volver a Reportes</button></div>';

  const tablas = crearTablaVigencia(datos.vigente, 'Cotizaciones Vigentes', '#10b981', '✅') + 
                 crearTablaVigencia(datos.porVencer, 'Cotizaciones Por Vencer (≤2 días)', '#f59e0b', '⚠️') + 
                 crearTablaVigencia(datos.vencida, 'Cotizaciones Vencidas', C.red, '❌') + 
                 (datos.sinVigencia && datos.sinVigencia.length > 0 ? crearTablaVigencia(datos.sinVigencia, 'Sin Fecha de Vigencia', C.muted, '—') : '');

  return header + resumenes + tablas;
}

export function renderReportes() {
  // Si está viendo reporte de vigencia
  if (state.reporteVigencia) {
    return renderReporteVigencia();
  }
  
  // Si está viendo detalle de una OP
  if (state.reporteDetalleOP) {
    return renderDetalleOP(state.reporteDetalleOP);
  }

  // Vista de lista con filtros
  const clientes = state.data.clientes || [];
  const ops = state.data.pedidos || [];
  
  // Inicializar filtros si no existen
  if (!state.reporteFiltros) state.reporteFiltros = {};
  
  // Aplicar filtros
  let opsFiltradasReportes = ops;
  
  if (state.reporteFiltros.cliente) {
    opsFiltradasReportes = opsFiltradasReportes.filter(op => 
      op.cliente_id === state.reporteFiltros.cliente
    );
  }
  
  if (state.reporteFiltros.op) {
    opsFiltradasReportes = opsFiltradasReportes.filter(op => 
      normalize(op.numero).includes(normalize(state.reporteFiltros.op))
    );
  }
  
  if (state.reporteFiltros.fecha) {
    opsFiltradasReportes = opsFiltradasReportes.filter(op => 
      op.fecha === state.reporteFiltros.fecha
    );
  }
  
  if (state.reporteFiltros.estado) {
    opsFiltradasReportes = opsFiltradasReportes.filter(op => 
      op.estado === state.reporteFiltros.estado
    );
  }

  const header = `
    <div style="margin-bottom:9px;display:flex;justify-content:space-between;align-items:center;gap:20px;">
      <div>
        <h2 style="margin:0 0 12px;font-size:16px;color:${C.text};font-weight:700;">📈 Reportería Avanzada</h2>
        <p style="margin:0;color:${C.muted};font-size:11.5px;">Análisis detallado de Órdenes de Producción</p>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
        <button onclick="exportarExcelOPs()" style="background:#16a34a;color:white;border:none;padding:8px 14px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;white-space:nowrap;">📥 Excel OPs</button>
        <button onclick="exportarExcelCT()" style="background:#16a34a;color:white;border:none;padding:8px 14px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;white-space:nowrap;">📥 Excel CT</button>
        <button data-action="ir-reporte-vigencia" style="background:linear-gradient(135deg,#12264D,#0B132B);border:none;color:white;padding:8px 14px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;white-space:nowrap;">📅 Vigencia CT</button>
      </div>
    </div>
  `;

  const filtros = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 9px;font-size:12px;color:${C.text};font-weight:700;">🔍 FILTROS</h3>
      
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:9px;">
        <div>
          <label style="display:block;font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">TERCERO (Cliente)</label>
          <select data-filter="cliente" style="width:100%;padding:8px;border:1px solid ${C.border};border-radius:6px;font-size:11.5px;">
            <option value="">— Todos los clientes —</option>
            ${clientes.map(c => `
              <option value="${c.id}" ${state.reporteFiltros.cliente === c.id ? 'selected' : ''}>
                ${escapeHTML(c.nombre)}
              </option>
            `).join('')}
          </select>
        </div>
        
        <div>
          <label style="display:block;font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">NÚMERO OP</label>
          <input type="text" data-filter="op" placeholder="OP-00001" value="${state.reporteFiltros.op || ''}" 
                 style="width:100%;padding:8px;border:1px solid ${C.border};border-radius:6px;font-size:11.5px;"/>
        </div>
        
        <div>
          <label style="display:block;font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">FECHA</label>
          <input type="date" data-filter="fecha" value="${state.reporteFiltros.fecha || ''}" 
                 style="width:100%;padding:8px;border:1px solid ${C.border};border-radius:6px;font-size:11.5px;"/>
        </div>
        
        <div>
          <label style="display:block;font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">ESTADO</label>
          <select data-filter="estado" style="width:100%;padding:8px;border:1px solid ${C.border};border-radius:6px;font-size:11.5px;">
            <option value="">— Todos los estados —</option>
            ${Object.entries(ESTADOS.OP || {}).map(([key, label]) => `
              <option value="${key}" ${state.reporteFiltros.estado === key ? 'selected' : ''}>
                ${label}
              </option>
            `).join('')}
          </select>
        </div>
        
        <div style="display:flex;align-items:flex-end;">
          <button data-action="limpiar-filtros-reportes" 
                  style="background:#e5e7eb;border:none;color:#374151;padding:8px 16px;border-radius:6px;font-size:11.5px;font-weight:700;cursor:pointer;width:100%;">
            🔄 Limpiar
          </button>
        </div>
      </div>
      
      <div style="margin-top:12px;font-size:11.5px;color:${C.muted};">
        Mostrando ${opsFiltradasReportes.length} de ${ops.length} órdenes
      </div>
    </div>
  `;

  if (opsFiltradasReportes.length === 0) {
    return header + filtros + `
      <div style="padding:60px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};">
        <div style="font-size:48px;margin-bottom:12px;">🔍</div>
        <div style="font-size:16px;font-weight:600;color:${C.text};margin-bottom:6px;">Sin resultados</div>
        <div style="font-size:12px;">Intenta con otros filtros</div>
      </div>
    `;
  }

  const rows = opsFiltradasReportes.map(op => {
    const cliente = clientes.find(c => c.id === op.cliente_id) || {};
    const remisiones = (state.data.remisiones || []).filter(rc => rc._origen && rc._origen.id === op.id);
    
    let cantTotal = 0, cantRemis = 0;
    (op.items || []).forEach(it => cantTotal += parseNumber(it.cantidad) || 0);
    remisiones.forEach(rc => {
      (rc.items || []).forEach(it => cantRemis += parseNumber(it.cantidad) || 0);
    });
    
    const porcentaje = cantTotal > 0 ? Math.round((cantRemis / cantTotal) * 100) : 0;
    let estadoRemision = "⚪ 0%";
    if (porcentaje > 0 && porcentaje < 100) estadoRemision = "🟡 " + porcentaje + "%";
    if (porcentaje === 100) estadoRemision = "🟢 100%";
    
    const estadoColor = ESTADO_COLOR[op.estado] || C.muted;
    
    return `
      <tr style="border-top:1px solid ${C.border};">
        <td style="padding:6px 10px;">
          <button data-action="ver-detalle-op-reportes" data-id="${op.id}" 
                  style="background:none;border:none;color:#4C5FA8;font-weight:700;cursor:pointer;text-decoration:underline;">
            ${escapeHTML(op.numero)}
          </button>
        </td>
        <td style="padding:6px 10px;">${escapeHTML(fmtDate(op.fecha))}</td>
        <td style="padding:6px 10px;">${escapeHTML(cliente.nombre || "—")}</td>
        <td style="padding:6px 10px;">
          <span style="background:${estadoColor}20;color:${estadoColor};padding:3px 9px;border-radius:6px;font-size:10.5px;font-weight:700;display:inline-block;">
            ${(ESTADOS.OP && ESTADOS.OP[op.estado]) || op.estado}
          </span>
        </td>
        <td style="padding:6px 10px;text-align:center;">
          <span style="background:#f3f4f6;padding:4px 8px;border-radius:6px;font-size:10.5px;font-weight:700;">
            ${estadoRemision}
          </span>
        </td>
        <td style="padding:6px 10px;text-align:center;font-size:11.5px;">
          <strong>${cantRemis}/${cantTotal}</strong>
        </td>
        <td style="padding:6px 10px;text-align:right;">
          <button data-action="ver-detalle-op-reportes" data-id="${op.id}" 
                  style="background:#4C5FA8;border:none;color:white;padding:6px 12px;border-radius:6px;font-size:11.5px;font-weight:700;cursor:pointer;">
            👁️ Detalles
          </button>
        </td>
      </tr>
    `;
  }).join("");

  return header + filtros + `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">OP</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Fecha</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Cliente</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Estado OP</th>
            <th style="padding:6px 10px;text-align:center;font-size:10.5px;font-weight:700;color:${C.text};">Remisión</th>
            <th style="padding:6px 10px;text-align:center;font-size:10.5px;font-weight:700;color:${C.text};">Unidades</th>
            <th style="padding:6px 10px;"></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "ver-detalle-op-reportes": (d, el, e) => {state.reporteDetalleOP = d.id;
      render();
      return;},
  "volver-reportes": (d, el, e) => {state.reporteDetalleOP = null;
      render();
      return;},
  "limpiar-filtros-reportes": (d, el, e) => {state.reporteFiltros = {};
      render();
      return;},
  "ir-reporte-vigencia": (d, el, e) => {state.reporteVigencia = true;
      render();
      return;},
  "cerrar-reporte-vigencia": (d, el, e) => {state.reporteVigencia = false;
      render();
      return;},
});
